// src/components/tokenPage/shared/TokenDashboardComponents.tsx

import React from "react";
import { formatLargeNumberCell, formatPercentage, formatPriceCell } from "@/utils/formatters";

interface PerfProps {
  label: string;
  value: number | null;
}

export const Perf: React.FC<PerfProps> = ({ label, value }) => (
  <div className={`rounded-lg px-3 py-2 ${value == null ? "bg-gray-800/60" : value >= 0 ? "bg-green-500/10" : "bg-red-500/10"}`}>
    <div className="text-xs text-gray-400">{label}</div>
    <div className={`text-sm font-semibold mt-0.5 ${value == null ? "text-gray-300" : value >= 0 ? "text-green-400" : "text-red-400"}`}>
      {value == null ? "N/A" : formatPercentage(value * 100)}
    </div>
  </div>
);

interface KpiBoxProps {
  label: string;
  value: string;
  sub?: string | undefined;
  subToneBySign?: boolean;
}

export const KpiBox: React.FC<KpiBoxProps> = ({ label, value, sub, subToneBySign = false }) => {
  const subClass = subToneBySign && typeof sub === 'string'
    ? sub.trim().startsWith('-')
      ? 'text-rose-400'
      : sub.trim().startsWith('+')
        ? 'text-emerald-400'
        : 'text-slate-400'
    : 'text-slate-400';
  return (
    <div className="bg-gray-800/40 rounded-lg p-2.5">
      <div className="text-[10px] text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-slate-50 tabular-nums">{value}</div>
      {sub && <div className={`text-[11px] ${subClass}`}>{sub}</div>}
    </div>
  );
};

interface MetricBoxProps {
  label: string;
  value: number | null;
  fmt: 'pct1' | 'pct2' | 'ratio2' | 'ratio4' | 'currency' | 'price' | 'int';
  toneBySign?: boolean;
  suffix?: string;
  toneOverride?: 'positive' | 'negative' | 'neutral' | undefined;
}

export const MetricBox: React.FC<MetricBoxProps> = ({ label, value, fmt, toneBySign = false, suffix, toneOverride }) => {
  // Hide if no value
  if (value == null) {
    return null;
  }

  const format = (v: number) => {
    if (fmt === 'currency') return formatLargeNumberCell(v, 2, '$');
    if (fmt === 'price') return formatPriceCell(v);
    if (fmt === 'pct1') return `${v.toFixed(1)}%`;
    if (fmt === 'pct2') return `${v.toFixed(2)}%`;
    if (fmt === 'ratio2') return v.toFixed(2);
    if (fmt === 'ratio4') return v.toFixed(4);
    if (fmt === 'int') return `${Math.round(v)}`;
    return String(v);
  };
  const toneAuto = toneBySign && value != null ? (value > 0 ? 'text-emerald-400' : value < 0 ? 'text-rose-400' : 'text-slate-300') : 'text-slate-50';
  const tone = toneOverride
    ? toneOverride === 'positive' ? 'text-emerald-400' : toneOverride === 'negative' ? 'text-rose-400' : 'text-slate-50'
    : toneAuto;
  return (
    <div className="bg-gray-800/40 rounded-lg p-2.5">
      <div className="text-[10px] text-slate-400">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${tone}`}>
        {`${format(value)}${suffix ?? ''}`}
      </div>
    </div>
  );
};

